import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { Tooltip } from './ui/Tooltip'
import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'

export default function NavBar({ onToggleSidebar }: { onToggleSidebar?: () => void }) {
  const navigate = useNavigate()
  const { user, logout, org, setOrg, workspace, setWorkspace } = useAuth()
  const isAdmin = !!(user?.roles?.some(r => r === 'ROLE_ADMIN' || r === 'ADMIN'))
  const [orgOpen, setOrgOpen] = useState(false)
  const [wsOpen, setWsOpen] = useState(false)
  const [userOpen, setUserOpen] = useState(false)
  const { isDark, setTheme, reducedMotion, setReducedMotion } = useTheme()
  const orgRef = useRef<HTMLDivElement | null>(null)
  const wsRef = useRef<HTMLDivElement | null>(null)
  const userRef = useRef<HTMLDivElement | null>(null)
  const orgButtonRef = useRef<HTMLButtonElement | null>(null)
  const wsButtonRef = useRef<HTMLButtonElement | null>(null)
  const userButtonRef = useRef<HTMLButtonElement | null>(null)
  const orgMenuRef = useRef<HTMLDivElement | null>(null)
  const wsMenuRef = useRef<HTMLDivElement | null>(null)
  const userMenuRef = useRef<HTMLDivElement | null>(null)
  const link = 'px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800'
  const active = ({ isActive }: { isActive: boolean }) => isActive ? `${link} bg-gray-100 dark:bg-gray-800` : link

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (orgRef.current && !orgRef.current.contains(e.target as Node)) setOrgOpen(false)
      if (wsRef.current && !wsRef.current.contains(e.target as Node)) setWsOpen(false)
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserOpen(false)
    }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [])

  // Helper: focus management within menu containers
  function getMenuItems(container?: HTMLElement | null): HTMLElement[] {
    if (!container) return []
    return Array.from(container.querySelectorAll<HTMLElement>('[role="menuitem"]'))
  }
  function focusItem(items: HTMLElement[], index: number) {
    if (!items.length) return
    const i = (index + items.length) % items.length
    items[i].focus()
  }
  type FocusTarget = { current: HTMLElement | null } | null | undefined

  function handleMenuKey(
    e: React.KeyboardEvent,
    containerRef: React.RefObject<HTMLDivElement>,
    close: () => void,
    focusTarget?: FocusTarget
  ) {
    const items = getMenuItems(containerRef.current || undefined)
    const currentIndex = items.findIndex(el => el === document.activeElement)
    if (e.key === 'ArrowDown') { e.preventDefault(); focusItem(items, currentIndex < 0 ? 0 : currentIndex + 1) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); focusItem(items, currentIndex < 0 ? items.length - 1 : currentIndex - 1) }
    else if (e.key === 'Home') { e.preventDefault(); focusItem(items, 0) }
    else if (e.key === 'End') { e.preventDefault(); focusItem(items, items.length - 1) }
    else if (e.key === 'Escape') {
      e.preventDefault()
      close()
      focusTarget?.current?.focus()
    }
  }

  type Org = { id: string; name: string }
  type Workspace = { id: string; name: string; orgId: string }
  const [orgs, setOrgs] = useState<Org[]>([])
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [searchQ, setSearchQ] = useState('')
  const [uploadingQuick, setUploadingQuick] = useState(false)
  const [uploadMessage, setUploadMessage] = useState('')
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/ui/orgs')
        setOrgs(data)
      } catch (error) {
        console.error('Failed to load organizations', error)
      }
    })()
  }, [])

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/ui/workspaces', { params: org ? { orgId: orgIdFor(org) } : {} })
        setWorkspaces(data)
      } catch (error) {
        console.error('Failed to load workspaces', error)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org])

  const orgIdFor = (name: string) => orgs.find(o => o.name === name)?.id || name

  // Top-level nav keyboard navigation (Left/Right/Home/End)
  const navRef = useRef<HTMLDivElement | null>(null)
  const onTopNavKey = (e: React.KeyboardEvent) => {
    const container = navRef.current
    if (!container) return
    const items = Array.from(container.querySelectorAll<HTMLElement>('[data-top-nav-item="true"]'))
    const currentIndex = items.findIndex(el => el === document.activeElement)
    if (['ArrowLeft','ArrowRight','Home','End'].includes(e.key)) e.preventDefault()
    if (e.key === 'ArrowRight') { const next = currentIndex < 0 ? 0 : currentIndex + 1; (items[(next)%items.length] || items[0])?.focus() }
    else if (e.key === 'ArrowLeft') { const prev = currentIndex < 0 ? items.length - 1 : currentIndex - 1; (items[(prev+items.length)%items.length] || items[0])?.focus() }
    else if (e.key === 'Home') { items[0]?.focus() }
    else if (e.key === 'End') { items[items.length - 1]?.focus() }
  }

  return (
    <header className="border-b border-gray-200 dark:border-gray-800">
      <div className="container flex items-center justify-between py-3">
        <div className="flex items-center gap-4">
          {/* hamburger for small screens */}
          <button aria-label="Toggle menu" onClick={() => onToggleSidebar && onToggleSidebar()} className="p-2 rounded md:hidden border">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700 dark:text-gray-200"><path d="M3 12h18M3 6h18M3 18h18"/></svg>
          </button>

          <Link to="/" className="font-semibold">VeriDocAI</Link>
          {/* Search (desktop) */}
          <div className="hidden md:flex items-center ml-4">
            <label htmlFor="global-search" className="sr-only">Search documents</label>
            <div className="relative">
              <input
                id="global-search"
                className="border rounded px-3 py-1 w-64 bg-white dark:bg-gray-900"
                placeholder="Search documents, clauses, people…"
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    const q = searchQ.trim()
                    if (q) navigate(`/search?q=${encodeURIComponent(q)}`)
                  } else if (e.key === 'Escape') {
                    setSearchQ('')
                  }
                }}
                aria-label="Global search"
              />
              <button aria-label="Search" onClick={() => { const q = searchQ.trim(); if (q) navigate(`/search?q=${encodeURIComponent(q)}`) }} className="absolute right-1 top-1.5 px-2 text-sm">🔎</button>
            </div>
          </div>
          <nav aria-label="Primary">
            <div
              className="flex md:hidden items-center gap-1"
              role="menubar"
              tabIndex={0}
              ref={navRef}
              onKeyDown={onTopNavKey}
            >
              <NavLink to="/upload" className={active} data-top-nav-item="true">Upload</NavLink>
              <NavLink to="/documents" className={active} data-top-nav-item="true">Documents</NavLink>
              <NavLink to="/analytics" className={active} data-top-nav-item="true">Analytics</NavLink>
              <NavLink to="/usage" className={active} data-top-nav-item="true">Usage</NavLink>
              <NavLink to="/search" className={active} data-top-nav-item="true">Search</NavLink>
              <NavLink to="/chat" className={active} data-top-nav-item="true">Chat</NavLink>
              <NavLink to="/workspaces" className={active} data-top-nav-item="true">Workspaces</NavLink>
              {isAdmin && <NavLink to="/users" className={active} data-top-nav-item="true">Users</NavLink>}
              <NavLink to="/plugins" className={active} data-top-nav-item="true">Plugins</NavLink>
              <NavLink to="/integrations" className={active} data-top-nav-item="true">Integrations</NavLink>
              <NavLink to="/connectors" className={active} data-top-nav-item="true">Connectors</NavLink>
              <NavLink to="/developers" className={active} data-top-nav-item="true">Developers</NavLink>
              <NavLink to="/marketplace" className={active} data-top-nav-item="true">Marketplace</NavLink>
              <NavLink to="/clause-explorer" className={active} data-top-nav-item="true">Clauses</NavLink>
              <NavLink to="/anomalies" className={active} data-top-nav-item="true">Anomalies</NavLink>
              <NavLink to="/model-accuracy" className={active} data-top-nav-item="true">Accuracy</NavLink>
              <NavLink to="/saved-views" className={active} data-top-nav-item="true">Views</NavLink>
              {isAdmin && <NavLink to="/billing" className={active} data-top-nav-item="true">Billing</NavLink>}
              {isAdmin && <NavLink to="/audit" className={active} data-top-nav-item="true">Audit</NavLink>}
              {isAdmin && <NavLink to="/flags" className={active} data-top-nav-item="true">Flags</NavLink>}
              {isAdmin && <NavLink to="/apikeys" className={active} data-top-nav-item="true">API Keys</NavLink>}
              {isAdmin && <NavLink to="/sessions" className={active} data-top-nav-item="true">Sessions</NavLink>}
              <NavLink to="/webhooks" className={active} data-top-nav-item="true">Webhooks</NavLink>
              {isAdmin && <NavLink to="/diagnostics" className={active} data-top-nav-item="true">Diagnostics</NavLink>}
            </div>
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <Tooltip content={`Switch to ${isDark ? 'light' : 'dark'} mode`}>
            <button className="px-3 py-2 rounded border" onClick={() => setTheme(isDark ? 'light' : 'dark')} aria-label="Toggle theme">
              {isDark ? '🌙' : '☀️'}
            </button>
          </Tooltip>
          <div className="hidden md:flex items-center gap-2">
            <div className="relative" ref={orgRef}>
              <Tooltip content="Select organization">
                <button
                  className="px-3 py-2 rounded border"
                  aria-haspopup="menu"
                  aria-expanded={orgOpen}
                  aria-controls="org-menu"
                  ref={orgButtonRef}
                  onClick={() => setOrgOpen(v => !v)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault()
                      setOrgOpen(true)
                      setTimeout(() => {
                        const items = orgMenuRef.current?.querySelectorAll<HTMLElement>('[role=menuitem]')
                        if (items && items.length) {
                          items[0].focus()
                        }
                      }, 0)
                    }
                  }}
                >
                  {org || 'Select Org'}
                </button>
              </Tooltip>
              {orgOpen && (
                <div
                  id="org-menu"
                  role="menu"
                  tabIndex={-1}
                  ref={orgMenuRef}
                  onKeyDown={(e) => handleMenuKey(e, orgMenuRef, () => setOrgOpen(false), orgButtonRef)}
                  className="absolute right-0 mt-1 min-w-[12rem] bg-white dark:bg-gray-900 border rounded shadow"
                >
                  {orgs.map(o => (
                    <button role="menuitem" tabIndex={-1} key={o.id} className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => { setOrg(o.name); setOrgOpen(false) }}>{o.name}</button>
                  ))}
                </div>
              )}
            </div>
            <div className="relative" ref={wsRef}>
              <Tooltip content="Select workspace">
                <button
                  className="px-3 py-2 rounded border"
                  aria-haspopup="menu"
                  aria-expanded={wsOpen}
                  aria-controls="ws-menu"
                  ref={wsButtonRef}
                  onClick={() => setWsOpen(v => !v)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault()
                      setWsOpen(true)
                      setTimeout(() => {
                        const items = wsMenuRef.current?.querySelectorAll<HTMLElement>('[role=menuitem]')
                        if (items && items.length) {
                          items[0].focus()
                        }
                      }, 0)
                    }
                  }}
                >
                  {workspace || 'Workspace'}
                </button>
              </Tooltip>
              {wsOpen && (
                <div
                  id="ws-menu"
                  role="menu"
                  tabIndex={-1}
                  ref={wsMenuRef}
                  onKeyDown={(e) => handleMenuKey(e, wsMenuRef, () => setWsOpen(false), wsButtonRef)}
                  className="absolute right-0 mt-1 min-w-[12rem] bg-white dark:bg-gray-900 border rounded shadow"
                >
                  {workspaces.map(w => (
                    <button role="menuitem" tabIndex={-1} key={w.id} className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => { setWorkspace(w.name); setWsOpen(false) }}>{w.name}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* Quick upload (desktop) */}
          <div className="hidden md:flex items-center gap-2">
            <button
              className="px-3 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Quick upload a document"
              disabled={uploadingQuick}
            >
              {uploadingQuick ? 'Uploading…' : 'Quick Upload'}
            </button>
            <input type="file" ref={fileInputRef} className="hidden" onChange={async (e) => {
              const f = e.target.files?.[0]
              if (!f) return
              setUploadingQuick(true)
              setUploadMessage('')
              try {
                const fd = new FormData()
                fd.append('file', f)
                fd.append('metadata', new Blob([JSON.stringify({ source: 'quick-upload-ui' })], { type: 'application/json' }))
                const { data } = await api.post('/ui/upload', fd, { headers: {} })
                const id = data?.id
                setUploadMessage(id ? `Uploaded ${f.name}` : 'Uploaded')
                // navigate to documents and include uploaded id as query param
                if (id) navigate(`/documents?uploaded=${encodeURIComponent(id)}`)
              } catch (err:any) {
                setUploadMessage(err?.message || String(err))
              } finally {
                setUploadingQuick(false)
                // clear the input so selecting the same file again will trigger change
                if (fileInputRef.current) fileInputRef.current.value = ''
              }
            }} />
            <div aria-live="polite" className="sr-only">{uploadMessage}</div>
          </div>
          <div className="relative" ref={userRef}>
            <button
              className="px-3 py-2 rounded border"
              aria-haspopup="menu"
              aria-expanded={userOpen}
              aria-controls="user-menu"
              ref={userButtonRef}
              onClick={() => setUserOpen(v => !v)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault()
                  setUserOpen(true)
                  setTimeout(() => {
                    const items = userMenuRef.current?.querySelectorAll<HTMLElement>('[role=menuitem]')
                    if (items && items.length) {
                      items[0].focus()
                    }
                  }, 0)
                }
              }}
            >
              <span className="text-sm">{user?.name}</span>
              {user?.roles?.length ? <span className="ml-2 text-xs text-gray-500">({user.roles[0]})</span> : null}
            </button>
            {userOpen && (
              <div
                id="user-menu"
                role="menu"
                tabIndex={-1}
                ref={userMenuRef}
                onKeyDown={(e) => handleMenuKey(e, userMenuRef, () => setUserOpen(false), userButtonRef)}
                className="absolute right-0 mt-1 min-w-[12rem] bg-white dark:bg-gray-900 border rounded shadow"
              >
                <div className="px-3 py-2 text-sm text-gray-500">Signed in as <b>{user?.name}</b></div>
                <hr className="border-t" />
                <NavLink to="/settings" role="menuitem" tabIndex={-1} className={({isActive}) => `block px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 ${isActive ? 'bg-gray-100 dark:bg-gray-800' : ''}`}>Settings</NavLink>
                <NavLink to="/whats-new" role="menuitem" tabIndex={-1} className={({isActive}) => `block px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 ${isActive ? 'bg-gray-100 dark:bg-gray-800' : ''}`}>What’s new?</NavLink>
                <div className="px-3 py-2 text-sm flex items-center justify-between">
                  <span>Reduce motion</span>
                  <button role="menuitem" tabIndex={-1} className="px-2 py-1 rounded border text-xs" onClick={() => setReducedMotion(!reducedMotion)}>{reducedMotion ? 'On' : 'Off'}</button>
                </div>
                <button role="menuitem" tabIndex={-1} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => { setOrgOpen(true); setUserOpen(false) }}>Switch Org…</button>
                <button
                  role="menuitem"
                  tabIndex={-1}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={async () => {
                    try {
                      await api.post('/ui/logout')
                    } catch (error) {
                      console.error('Logout failed', error)
                    } finally {
                      logout()
                    }
                  }}
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
